// frontend/src/pages/Cashier.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  Grid,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  AddShoppingCart,
  Delete,
  DeleteSweep,
  LocalPrintshop,
  WorkspacePremium,
  Add,
  Remove,
} from "@mui/icons-material";
import API from "../api";
import { printReceipt } from "../utils/printer";

const INITIAL_AUTH_DIALOG = { open: false, type: "", targetId: null, pendingQty: null };

export default function Cashier({ loggedUser, setActiveTab, onNavigate }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [inputQty, setInputQty] = useState(1);

  const [tendered, setTendered] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // 💳 License Expiration Notification Modal State
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

  // 🔐 Supervisor Override State
  const [authDialog, setAuthDialog] = useState(INITIAL_AUTH_DIALOG);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");

  // 🏢 Active workspace resolution
  const getActiveTenantId = () => localStorage.getItem("tenant_id") || "default";

  // 🔑 Auth header builder helper
  const getRequestHeaders = useCallback(() => {
    const headers = { "X-Tenant-ID": getActiveTenantId() };
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  // 🖩 Computed Financial Totals
  const total = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity * item.price, 0);
  }, [cart]);

  const change = useMemo(() => {
    const parsedTendered = parseFloat(tendered) || 0;
    return parsedTendered - total;
  }, [tendered, total]);

  // 🔄 Navigation Handler to Billing/Licensing Tab
  const navigateToBilling = useCallback(() => {
    if (typeof setActiveTab === "function") {
      setActiveTab("billing");
      return;
    }

    if (typeof onNavigate === "function") {
      onNavigate("billing");
      return;
    }

    // Fallback for React Router
    if (window.location.pathname.includes("/dashboard")) {
      window.location.href = "/dashboard/billing";
      return;
    }

    // Fallback for hash routing or window events
    window.location.hash = "billing";
    window.dispatchEvent(new CustomEvent("switchTab", { detail: "billing" }));
  }, [setActiveTab, onNavigate]);

  // 💳 License Expiration Notification Handler
  const handleLicenseExpiration = useCallback(() => {
    setLicenseDialogOpen(true);
    setError("🚫 Workspace Unlicensed: License has expired. Redirecting to billing options...");
  }, []);

  // 📦 Fetch Products and Purge Stale Items
  const fetchProducts = useCallback(async () => {
    try {
      const res = await API.get("/products/", {
        headers: getRequestHeaders(),
      });
      const fetchedProducts = res.data || [];
      setProducts(fetchedProducts);
      setError("");

      setCart((prevCart) => {
        if (prevCart.length === 0) return prevCart;
        const validIds = new Set(fetchedProducts.map((p) => p.id));
        return prevCart.filter((item) => validIds.has(item.product_id));
      });
    } catch (err) {
      console.error("Failed to fetch products:", err);
      if (err.response?.status === 402) {
        handleLicenseExpiration();
      } else if (err.response?.status === 403) {
        setError(
          err.response?.data?.detail ||
            "🔒 Access Forbidden: Your role (CASHIER) does not have clearance for this action in this workspace."
        );
      } else {
        setError("Failed to load product catalog.");
      }
    }
  }, [getRequestHeaders, handleLicenseExpiration]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ➕ Add Item to Cart
  const handleAddToCart = () => {
    if (!selectedProductId) return;
    const targetProduct = products.find((p) => p.id === selectedProductId);
    if (!targetProduct) return;

    if (inputQty <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }

    if (inputQty > targetProduct.quantity) {
      setError(`Insufficient stock! Only ${targetProduct.quantity} units available.`);
      return;
    }

    setCart((prev) => {
      const existingIdx = prev.findIndex((item) => item.product_id === targetProduct.id);
      const updated = [...prev];

      if (existingIdx >= 0) {
        const cumulativeQty = updated[existingIdx].quantity + inputQty;
        if (cumulativeQty > targetProduct.quantity) {
          setError(`Cannot add item. Total quantity exceeds stock limit (${targetProduct.quantity}).`);
          return prev;
        }
        updated[existingIdx].quantity = cumulativeQty;
      } else {
        updated.push({
          product_id: targetProduct.id,
          productname: targetProduct.productname,
          quantity: inputQty,
          price: targetProduct.price,
          is_taxable: targetProduct.is_taxable,
          vat_rate: targetProduct.vat_rate,
        });
      }
      setError("");
      return updated;
    });

    setSelectedProductId("");
    setInputQty(1);
  };

  // 🛒 Direct Quantity Modification Handlers
  const handleUpdateQuantity = (productId, targetQty) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newQty = parseInt(targetQty, 10);

    // If reduced to 0 or below, trigger supervisor authorization to void/remove line item
    if (isNaN(newQty) || newQty <= 0) {
      requestVoidAuthorization("item", productId);
      return;
    }

    if (newQty > product.quantity) {
      setError(`Cannot set quantity. Stock limit is ${product.quantity} units.`);
      return;
    }

    setError("");
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId ? { ...item, quantity: newQty } : item
      )
    );
  };

  // 🛡️ Open Void Authorization Dialog
  const requestVoidAuthorization = (type, targetId = null, pendingQty = null) => {
    setAuthError("");
    setAuthEmail("");
    setAuthPassword("");
    setAuthDialog({ open: true, type, targetId, pendingQty });
  };

  // 🔐 Verify Supervisor Credentials
  const handleVerifyOverride = async () => {
    setAuthError("");
    try {
      const res = await API.post(
        "/login",
        { username: authEmail, password: authPassword },
        { headers: getRequestHeaders() }
      );

      const userRole = (res.data?.user?.role || res.data?.role || "").toLowerCase();
      const isAuthorized = ["a", "s", "admin", "supervisor", "manager"].includes(userRole);

      if (isAuthorized) {
        if (authDialog.type === "item") {
          setCart((prev) => prev.filter((item) => item.product_id !== authDialog.targetId));
        } else if (authDialog.type === "all") {
          setCart([]);
        }
        setAuthDialog(INITIAL_AUTH_DIALOG);
        setError("");
      } else {
        setAuthError("🚫 Security Denied: Account lacks Supervisor privileges.");
      }
    } catch (err) {
      console.error("Override Failure:", err);
      setAuthError(err.response?.data?.detail || "❌ Authentication rejected: Invalid credentials.");
    }
  };

  // 💳 Submit Sale
  const handleFinalizeSale = async () => {
    if (cart.length === 0) {
      setError("Transaction rejected: Shopping cart is empty.");
      return;
    }

    const currentTendered = parseFloat(tendered) || 0;
    if (currentTendered < total) {
      setError("Transaction rejected: Tendered amount is less than total due.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    const payload = {
      user_id: loggedUser?.id || loggedUser?.user_id,
      cart: cart.map((item) => ({ product_id: item.product_id, quantity: item.quantity })),
      total,
      tendered: currentTendered,
      change,
      payment_method: paymentMethod,
    };

    try {
      const res = await API.post("/sales/", payload, {
        headers: getRequestHeaders(),
      });
      setSuccessMsg(`✅ Sale Complete: Order #${res.data.id || res.data.sale_id}`);

      try {
        await printReceipt({ sale: res.data });
      } catch (printError) {
        setError("Sale recorded, but local receipt printer did not respond.");
      }

      setCart([]);
      setTendered("");
      setPaymentMethod("cash");
      fetchProducts();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 402) {
        handleLicenseExpiration();
      } else if (err.response?.status === 403) {
        setError(
          err.response?.data?.detail ||
            "🚫 Access Forbidden: Cashier role does not have permission to post sales in this workspace."
        );
      } else {
        setError(err.response?.data?.detail || "An unexpected error occurred while processing the sale.");
      }
    } finally {
      setLoading(false);
    }
  };

  const isCheckoutDisabled = loading || cart.length === 0 || (parseFloat(tendered) || 0) < total;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          💳 Commercial Point of Sale
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active Workspace: <strong>{getActiveTenantId().toUpperCase()}</strong>
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            error.includes("License") || error.includes("Unlicensed") ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<WorkspacePremium />}
                onClick={navigateToBilling}
              >
                Select License Tier
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}

      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      <Grid container spacing={3}>
        {/* Left Control Column: Item Selection and Cart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 2, mb: 3, backgroundColor: "action.hover" }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>📦 Product Selector</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "background.paper" }}
                >
                  <MenuItem value="" disabled>-- Scan or Select Product --</MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id} disabled={p.quantity <= 0}>
                      {p.productname} (${p.price?.toFixed(2)}) — [Stock: {p.quantity}] {p.quantity <= 0 ? "(OUT)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  value={inputQty}
                  onChange={(e) => setInputQty(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddShoppingCart />}
                  onClick={handleAddToCart}
                  disabled={!selectedProductId}
                  fullWidth
                >
                  Add to Cart
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Active Shopping Session Cart Table View */}
          <Paper elevation={3}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h5" fontWeight="bold">🛒 Active Cart</Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                disabled={cart.length === 0}
                onClick={() => requestVoidAuthorization("all")}
              >
                Void Basket
              </Button>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "text.primary" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Product Details</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Unit Price</TableCell>

                  {/* Quantity Control Column Header */}
                  <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", width: "180px" }}>
                    Quantity
                  </TableCell>

                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Total</TableCell>
                  <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cart.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                      Cart is empty. Select products to begin.
                    </TableCell>
                  </TableRow>
                ) : (
                  cart.map((item) => (
                    <TableRow key={item.product_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{item.productname}</TableCell>
                      <TableCell>${item.price?.toFixed(2)}</TableCell>

                      {/* Interactive Quantity Control Cell */}
                      <TableCell align="center">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            sx={{ border: "1px solid", borderColor: "divider" }}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => handleUpdateQuantity(item.product_id, e.target.value)}
                            inputProps={{
                              min: 1,
                              style: { textAlign: "center", width: "42px", padding: "4px" },
                            }}
                            variant="outlined"
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                            sx={{ border: "1px solid", borderColor: "divider" }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold" }}>${(item.quantity * item.price).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => requestVoidAuthorization("item", item.product_id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Right Financial Settlement Control Deck Column */}
        <Grid item xs={12} md={4}>
          <Paper elevation={4} sx={{ p: 3, backgroundColor: "#1e1e1e", color: "#fff", borderRadius: "8px" }}>
            <Typography variant="h5" fontWeight="bold" borderBottom="1px solid #444" pb={1} mb={3}>
              💰 Settlement
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body1" color="#aaa">Line Items:</Typography>
              <Typography variant="body1" fontWeight="bold">{cart.length}</Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", mb: 3, backgroundColor: "#2a2a2a", p: 1.5, borderRadius: "6px", borderLeft: "4px solid #00e676" }}>
              <Typography variant="subtitle2" color="#00e676" sx={{ letterSpacing: "1px", fontWeight: "bold", textTransform: "uppercase" }}>
                Total Due
              </Typography>
              <Typography variant="h3" color="#00e676" fontWeight="900" sx={{ fontFamily: "monospace", textAlign: "right", mt: 0.5 }}>
                ${total.toFixed(2)}
              </Typography>
            </Box>

            <TextField
              label="Tendered Amount ($)"
              type="number"
              variant="outlined"
              fullWidth
              value={tendered}
              onChange={(e) => setTendered(e.target.value)}
             inputProps={{
  style: {
    color: "#ffffff",
    WebkitTextFillColor: "#ffffff",
    caretColor: "#ffffff",
    fontSize: "1.4rem",
    fontWeight: "bold",
    fontFamily: "monospace",
  },
}}
              InputLabelProps={{ style: { color: "#00e676", fontWeight: "bold" } }}
              sx={styles.tenderedInput}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                mb: 3,
                backgroundColor: "#2a2a2a",
                p: 1.5,
                borderRadius: "6px",
                borderLeft: change >= 0 ? "4px solid #ff9100" : "4px solid #f44336",
              }}
            >
              <Typography
                variant="subtitle2"
                color={change >= 0 ? "#ff9100" : "#f44336"}
                sx={{ letterSpacing: "1px", fontWeight: "bold", textTransform: "uppercase" }}
              >
                Change Due
              </Typography>
              <Typography
                variant="h4"
                color={change >= 0 ? "#ff9100" : "#f44336"}
                fontWeight="900"
                sx={{ fontFamily: "monospace", textAlign: "right", mt: 0.5 }}
              >
                ${change >= 0 && tendered !== "" ? change.toFixed(2) : "0.00"}
              </Typography>
            </Box>

            <Typography variant="body2" color="#aaa" gutterBottom>Payment Method</Typography>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              fullWidth
              sx={{ color: "#fff", backgroundColor: "#2d2d2d", mb: 4, height: "45px" }}
            >
              <MenuItem value="cash">💵 Cash</MenuItem>
              <MenuItem value="card">💳 Card / Swipe</MenuItem>
              <MenuItem value="mobile">📱 Mobile Transfer</MenuItem>
            </Select>

            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LocalPrintshop />}
              onClick={handleFinalizeSale}
              disabled={isCheckoutDisabled}
              sx={{ py: 1.5, fontWeight: "bold", fontSize: "1.1rem" }}
            >
              Confirm & Print Invoice
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* 💳 LICENSE EXPIRED NOTIFICATION DIALOG */}
      <Dialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>
          ⚠️ Workspace License Expired
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your current workspace license has expired or is invalid.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please choose a suitable plan in the Licensing & Billing tab to activate full features and continue processing transactions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLicenseDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setLicenseDialogOpen(false);
              navigateToBilling();
            }}
            variant="contained"
            color="primary"
            startIcon={<WorkspacePremium />}
          >
            Select Subscription Tier
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔐 SUPERVISOR OVERRIDE DIALOG */}
      <Dialog open={authDialog.open} onClose={() => setAuthDialog(INITIAL_AUTH_DIALOG)}>
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>🛡️ Supervisor Authorization Required</DialogTitle>
        <DialogContent sx={{ minWidth: "300px", display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {authError && <Alert severity="error">{authError}</Alert>}
          <Typography variant="body2" color="textSecondary">
            This action requires supervisor credentials to proceed.
          </Typography>
          <TextField
            label="Supervisor Username / Email"
            variant="outlined"
            fullWidth
            value={authEmail}
            onChange={(e) => setAuthEmail(e.target.value)}
          />
          <TextField
            label="Password / PIN"
            type="password"
            variant="outlined"
            fullWidth
            value={authPassword}
            onChange={(e) => setAuthPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAuthDialog(INITIAL_AUTH_DIALOG)} color="inherit">Cancel</Button>
          <Button onClick={handleVerifyOverride} variant="contained" color="error">
            Verify & Void
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

const styles = {
  tenderedInput: {
    mb: 3,

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#000000 !important",
      borderRadius: "4px",

      "& fieldset": {
        borderColor: "#444444 !important",
      },

      "&:hover fieldset": {
        borderColor: "#00e676 !important",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#00e676 !important",
      },
    },

    "& .MuiInputBase-input": {
      color: "#ffffff !important",
      WebkitTextFillColor: "#ffffff !important",
      caretColor: "#ffffff",
    },
  },
};